import { hydrate } from 'svelte';
import { overallScores, group, refreshDate, getwebScore, GetIssueList, getWebsites, GetWebGraph } from '../api';
import { Gauge, LineGraph, TopIssues, WebOverview, WebsitesTable } from '../../components';
import { makeConversation } from '../fn';
import type { WebState } from '.';

const segment = 'web';

let webState: WebState = $state({
  tableData: [],
  gauge: 0,
  prevScore: 0,
  percentChange: 0,
  percentChangePolarity: 'zero',
  refreshDate: undefined,
  topIssues: [],
  topIssuesLoading: true,
  graphData: [],
  overview: {
    pages: 0,
    PDFs: 0,
    images: 0,
    brokenLinks: 0,
  }
});

const DOM = {
  gauge: document.getElementById('uc-gauge'),
  graph: document.getElementById('graph'),
  topIssues: document.getElementById('top-issues'),
  webOverview: document.getElementById('overview'),
  sites: document.getElementById('uc-tabulator'),
}

async function fetchEverythingWeb(segment: string, groupCode: string) {
  const [webDataResponse, webSitesResponse, issueListResponse, graphDataResponse] = await Promise.all([
    fetch(`${getwebScore}/${groupCode}`),
    fetch(`${getWebsites}/${groupCode}`),
    fetch(`${GetIssueList}/${groupCode}`),
    fetch(`${GetWebGraph}/${groupCode}`)
  ]);

  const webDataRes = await webDataResponse.json();
  const webSitesRes = await webSitesResponse.json();
  const issueListRes = await issueListResponse.json();
  const graphDataRes = await graphDataResponse.json();
  //console.log('API Responses:', { webDataRes, webSitesRes, issueListRes, graphDataRes });

  return [webDataRes, webSitesRes, issueListRes, graphDataRes];
}

async function mountComponents(segment: string, groupCode: string) {
  if(DOM.gauge) {
    hydrate(Gauge, {
      target: DOM.gauge,
      props: {
        dashboardState: webState,
        segment,
      }
    });
  }

  if(DOM.graph) {
    hydrate(LineGraph, {
      target: DOM.graph,
      props: {
        dashboardState: webState,
        segment,
      }
    });
  }

  if(DOM.topIssues) {
    hydrate(TopIssues, {
      target: DOM.topIssues,
      props: {
        title: 'Top 5 Issues',
        dashboardState: webState,
      }
    });
  }

  if(DOM.webOverview) {
    hydrate(WebOverview, {
      target: DOM.webOverview,
      props: {
        dashboardState: webState,
      }
    });
  }

  if(DOM.sites) {
    hydrate(WebsitesTable, {
      target: DOM.sites,
      props: {
        tableTitle: 'Website List',
        dashboardState: webState,
        loading: false,
        error: false
      }
    });
  }
}

export async function buildWeb(segment: string, groupCode: string|undefined = 'uc') {
  console.log('config', {gc: groupCode, segment});
  console.log('building web');
  console.log('init', $state.snapshot(webState.overview));
  
  mountComponents(segment, groupCode);
  makeConversation();

  try {
    const [webData, webSites, issueList, graphData] = await fetchEverythingWeb(segment, groupCode);
    
    console.log('Raw webSites data:', webSites);
    
    // Process websites data for the table
    if (webSites && webSites.data && Array.isArray(webSites.data)) {
      webState.tableData = webSites.data.map((site: any) => {
        console.log('Processing site:', site);
        return {
          SiteName: site.SiteName || site.Name || site.siteName || site.name || 'Unknown Site',
          SiteUrl: site.SiteUrl || site.Url || site.siteUrl || site.url || ''
        };
      });
    } else if (webSites && Array.isArray(webSites)) {
      webState.tableData = webSites.map((site: any) => ({
        SiteName: site.SiteName || site.Name || site.siteName || site.name || 'Unknown Site',
        SiteUrl: site.SiteUrl || site.Url || site.siteUrl || site.url || ''
      }));
    } else {
      console.warn('No valid website data found:', webSites);
      webState.tableData = [];
    }
    
    console.log('Final tableData:', $state.snapshot(webState.tableData));
    
    // Process other data...
    if (webData && webData.data && webData.data[0]) {
      const webDataItem = webData.data[0];
      webState.gauge = parseFloat(webDataItem.SIOverallScore || "0");
      
      webState.overview = {
        pages: parseInt(webDataItem.SIOverviewPageCount || "0", 10),
        PDFs: parseInt(webDataItem.SIPdfCount || "0", 10),
        images: parseInt(webDataItem.SIImageCount || "0", 10),
        brokenLinks: parseInt(webDataItem.SIBrokenLinkCount || "0", 10),
      };
    }
    
    // Process graph data
    if (graphData && graphData.data) {
      webState.graphData = graphData;
    }
    
    // Process issues data - transform to {text, count} structure for TopIssues component
    if (issueList && issueList.data && Array.isArray(issueList.data)) {
      console.log('Raw issues data:', issueList.data);
      webState.topIssues = issueList.data.slice(0, 5).map((issue: any, index : number) => {
        console.log('Processing issue:', issue);
        
        if (typeof issue === 'object' && issue !== null) {
          // Handle the API structure with IssueDescription and IssueCount
          if (issue.IssueDescription) {
            return {
              text: issue.IssueDescription,
              count: issue.IssueCount
            };
          }
        }
        
        // Fallback for any other format
        return {
          text: typeof issue === 'string' ? issue : `Issue ${index + 1}`,
          count: '0'
        };
      });
    } else {
      console.warn('No valid issues data found:', issueList);
      webState.topIssues = [];
    }
    webState.topIssuesLoading = false;
    
    console.log('Processed topIssues:', $state.snapshot(webState.topIssues));
    console.log('update', $state.snapshot(webState.overview));
    
  } catch (error) {
    console.error('Error building web dashboard:', error);
    webState.topIssuesLoading = false;
  }
}